import * as React from 'react';
import type { AgencyUserFieldsFragment, LoggedInAgencyUserFieldsFragment } from '../api/graphql';

type CurrentUserContextValue = {
  agencyUser: LoggedInAgencyUserFieldsFragment;
  agencyUsers: AgencyUserFieldsFragment[];
};

const CurrentUserContext = React.createContext<CurrentUserContextValue | null>(null);

/**
 * Provides the currently-signed-in agency user and agency user list to descendants.
 * Mounted in the same Await as the root app loader so both are resolved before
 * descendants call useCurrentAgencyUser() or useAgencyUsers().
 */
export const CurrentUserProvider: React.FC<{
  agencyUser: LoggedInAgencyUserFieldsFragment;
  agencyUsers: AgencyUserFieldsFragment[];
  children: React.ReactNode;
}> = ({ agencyUser, agencyUsers, children }) => {
  const value = React.useMemo(() => ({ agencyUser, agencyUsers }), [agencyUser, agencyUsers]);
  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
};

export const useCurrentAgencyUser = () => {
  const context = React.useContext(CurrentUserContext);
  if (!context) {
    throw new Error('useCurrentAgencyUser must be used within a CurrentUserProvider');
  }
  return context.agencyUser;
};

export const useCurrentAgencyUserId = () => useCurrentAgencyUser().id;

export const useAgencyUsers = () => {
  const context = React.useContext(CurrentUserContext);
  if (!context) {
    throw new Error('useAgencyUsers must be used within a CurrentUserProvider');
  }
  return context.agencyUsers;
};

/**
 * Non-throwing variant for components rendered outside the dashboard tree
 * (e.g. unit tests, error boundaries, ad-hoc previews). Returns null when no
 * provider is present so callers can degrade gracefully.
 */
export const useOptionalCurrentAgencyUser = () => {
  const context = React.useContext(CurrentUserContext);
  return context?.agencyUser ?? null;
};
